import React, { useMemo } from 'react';
import { useFormik } from 'formik';
import { Yup } from '../../../helpers/Yup';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { Button } from '../../../components/UI-CHAKRA/Button';
import { FormControl } from '../../../components/UI-CHAKRA/FormControl';
import { FormLabel } from '../../../components/UI-CHAKRA/FormLabel';
import { Text } from '../../../components/UI-CHAKRA/Text';
import { FormHelperText } from '@chakra-ui/react';
import { Checkbox } from '../../../components/UI-CHAKRA/Checkbox';
import { PrescriptionInteractionJustifyDetail } from '../service';
import { InteractionsHelpers } from '../../../components/interactions/InteractionsHelpers';
import Textarea from '../../../components/UI-CHAKRA/Textarea';
import { ReactComponent as RightArrow } from '../../../assets/images/icons/right-arrow.svg';
import { capitalizeFirstLetter } from '../../../helpers/string';

enum SEVERITIES {
  CONTRAINDICATED = 'contraindicada',
  MAJOR = 'grave',
  MODERATE = 'moderada',
  MINOR = 'leve',
}

interface StepJustificationProps {
  goBack(): void;
  next(interaction: PrescriptionInteractionJustifyDetail): void;
  interaction: PrescriptionInteractionJustifyDetail;
  currentStep: number;
  numberOfSteps: number;
  isLoading: boolean;
}

const OTHER_INTERACTIONS_LABEL = {
  PREGNANCY: 'Gestante',
  LACTATION: 'Lactante',
  FOOD: 'Alimento',
  LAB: 'Exame',
  ETHANOL: 'Álcool',
  TOBACCO: 'Tabaco',
  INGREDIENT_DUPLICATION: 'Duplicidade de fármaco',
  ALLERGY: 'Alergia',
};

const colors = InteractionsHelpers.getColorsInteractions();

interface InteractionChipProps {
  backgroundColor: string;
  color: string;
  label: string | null;
}

const InteractionChip = (props: InteractionChipProps) => {
  return (
    <Box bg={props.backgroundColor} borderRadius={'30px'} p={'2px 12px'} mr={'8px'}>
      <Text textAlign={'center'} lineHeight={'15px'} fontSize={'13px'} fontWeight={600} color={props.color}>
        {props.label}
      </Text>
    </Box>
  );
};

const StepJustification = (props: StepJustificationProps) => {
  const { goBack, interaction } = props;
  const interactionColor = colors[interaction.severity];

  const formik = useFormik({
    validateOnChange: false,
    initialValues: {
      actionText: interaction.justification || '',
      actionType: interaction.requiredAction || 'Justificativa',
      consentCheckbox: !!interaction.justification || false,
    },
    validationSchema: Yup.object().shape({
      actionText: Yup.string().when('actionType', {
        is: 'Justificativa',
        then: (schema) => schema.required('Informe uma justificativa válida.').trim(),
        otherwise: (schema) => schema.nullable(),
      }),
      consentCheckbox: Yup.bool().when('actionType', {
        is: 'Notificação',
        then: (schema) => schema.required('Consentimento necessário.'),
        otherwise: (schema) => schema.nullable(),
      }),
    }),
    onSubmit: (values) => {
      switch (interaction.requiredAction) {
        case 'Justificativa':
          return props.next({ ...interaction, justification: values.actionText });
        case 'Notificação':
          return props.next({
            ...interaction,
            justification: 'Estou ciente e concordo em seguir com essa prescrição',
          });
        case 'N/A':
          return props.next({ ...interaction, justification: '' });
        default:
          return props.next({ ...interaction, justification: '' });
      }
    },
  });

  const validateField = useMemo(() => formik.validateField, [formik.validateField]);
  const setFieldValue = useMemo(() => formik.setFieldValue, [formik.setFieldValue]);
  const handleSubmit = useMemo(() => formik.handleSubmit, [formik.handleSubmit]);

  const JustifyInput = useMemo(() => {
    return (
      <FormControl>
        <FormLabel mb={0} htmlFor="email" fontFamily={'Ambit-Regular'} fontSize={'14px'}>
          Justificativa*
        </FormLabel>
        <Textarea
          autoFocus
          value={formik.values.actionText}
          onChange={formik.handleChange}
          placeholder="Digite aqui a justificativa da sua decisão"
          name="actionText"
          isInvalid={!!formik.errors.actionText}
          onBlur={() => validateField('actionText')}
          resize={'none'}
          maxLength={500}
          data-testid={'justify-input'}
        />
        <FormHelperText color={'gray.main'} textAlign={'right'}>
          {formik.values.actionText.length}/500
        </FormHelperText>
      </FormControl>
    );
  }, [formik.values, formik.errors.actionText, formik.handleChange, validateField]);

  const JustifyCheckBox = useMemo(() => {
    return (
      <Box p={'10px 10px'} h={'100%'} display={'flex'} alignItems={'center'}>
        <Checkbox
          defaultChecked={formik.values.consentCheckbox}
          value={String(formik.values.consentCheckbox)}
          onChange={() => {
            if (!formik.values.consentCheckbox) {
              handleSubmit();
            }

            setFieldValue('consentCheckbox', !formik.values.consentCheckbox);
          }}
          fontWeight={400}
          fontSize={'14px'}
          lineHeight={'15px'}
        >
          Estou ciente e concordo em seguir com essa prescrição
        </Checkbox>
      </Box>
    );
  }, [formik.values.consentCheckbox, setFieldValue, handleSubmit]);

  const Consent = useMemo(() => {
    switch (interaction.requiredAction) {
      case 'Justificativa':
        return JustifyInput;
      case 'Notificação':
        return JustifyCheckBox;
      case 'N/A':
        return;
      default:
        return JustifyInput;
    }
  }, [interaction.requiredAction, JustifyInput, JustifyCheckBox]);

  const isNextButtonDisabled = useMemo(() => {
    if (interaction.requiredAction === 'Justificativa') {
      return formik.values.actionText.trim().length === 0;
    }

    if (interaction.requiredAction === 'Notificação') {
      return !formik.values.consentCheckbox;
    }

    return false;
  }, [formik.values.actionText, formik.values.consentCheckbox, interaction.requiredAction]);

  const MedicineNameChip = useMemo(() => {
    return (
      <Box display={'flex'} alignItems={'center'}>
        <InteractionChip
          backgroundColor={interactionColor.backgroundColor}
          color={interactionColor.fontColor}
          label={capitalizeFirstLetter(interaction.firstMedicineName)}
        />

        <Text fontFamily={'Ambit-Regular'} fontWeight={700} fontSize={'14px'} mr={'8px'}>
          com
        </Text>

        {interaction.secondMedicineName && interaction.type === 'DRUG' && (
          <InteractionChip
            backgroundColor={interactionColor.backgroundColor}
            color={interactionColor.fontColor}
            label={capitalizeFirstLetter(interaction.secondMedicineName)}
          />
        )}

        {!interaction.secondMedicineName && interaction.type !== 'DRUG' && (
          <InteractionChip
            backgroundColor={interactionColor.backgroundColor}
            color={interactionColor.fontColor}
            label={OTHER_INTERACTIONS_LABEL[interaction.type as keyof typeof OTHER_INTERACTIONS_LABEL]}
          />
        )}
      </Box>
    );
  }, [interactionColor, interaction]);

  const interactionSeverity = useMemo(() => {
    return `Interação ${SEVERITIES[interaction.severity as keyof typeof SEVERITIES]}`;
  }, [interaction.severity]);

  const buttonLabel = useMemo(() => {
    return props.numberOfSteps > props.currentStep ? 'Próxima' : 'Concluir';
  }, [props.numberOfSteps, props.currentStep]);

  return (
    <form
      onSubmit={formik.handleSubmit}
      style={{
        width: '100%',
      }}
    >
      <Box>
        <Text
          data-testid={interactionSeverity}
          mb={'10px'}
          fontFamily={'Ambit-Regular'}
          fontSize={'18px'}
          fontWeight={700}
        >
          {interactionSeverity}
        </Text>
        {MedicineNameChip}

        <Box display={'flex'} h={'200px'} flexDirection={'column'} justifyContent={'space-between'}>
          <Box p={'10px 0'}>{Consent}</Box>

          <Box width={'100%'} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
            <Button variant={'outline'} onClick={goBack} borderRadius={'40px'}>
              Voltar
            </Button>
            <Text color={'gray.main'}>{`${props.currentStep}/${props.numberOfSteps}`}</Text>
            <Button
              isLoading={props.isLoading}
              rightIcon={<RightArrow />}
              type={'submit'}
              isDisabled={isNextButtonDisabled || props.isLoading}
              borderRadius={'40px'}
            >
              {buttonLabel}
            </Button>
          </Box>
        </Box>
      </Box>
    </form>
  );
};

export default StepJustification;
